import { MultiSelectConfig, DataLoadMode, FilterMode } from './generic-multiselect.component';
import { SKILL_OPTIONS } from '@shared/enums/study-plan.enum';
import { EMPLOYEE_POSITION_STATUS_OPTIONS } from '@shared/enums/employee-position-status.enum';

/**
 * Generic MultiSelect Configurations
 *
 * Usage:
 * <generic-p-multiselect [config]="skillsMultiSelectConfig"></generic-p-multiselect>
 */

// Skills Multi-Select Configuration
export const SKILLS_MULTISELECT_CONFIG: MultiSelectConfig = {
    dataLoadMode: DataLoadMode.GET_ALL,
    staticOptions: SKILL_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn kỹ năng',
    templateName: 'skill'
    // virtualScroll: auto-enabled if options.length > 100
    // lazy: always false
};

// Employee Position Status Multi-Select Configuration
export const EMPLOYEE_POSITION_STATUS_MULTISELECT_CONFIG: MultiSelectConfig = {
    dataLoadMode: DataLoadMode.GET_ALL,
    staticOptions: EMPLOYEE_POSITION_STATUS_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn trạng thái',
    templateName: 'employeePositionStatus'
    // virtualScroll: auto-enabled if options.length > 100
    // lazy: always false
};

// Learner Multi-Select Configuration
export const LEARNER_MULTISELECT_CONFIG: MultiSelectConfig = {
    dataLoadMode: DataLoadMode.GET_PAGINATED,
    filterMode: FilterMode.SERVER_SIDE,
    service: null, // Will be injected
    method: 'getLearnerOptions',
    idField: 'id',
    labelFormatter: (learner: any) => `${learner.code} - ${learner.fullname}`,
    valueField: 'id',
    placeholder: 'Chọn học viên',
    sortField: 'code,asc',
    templateName: 'learner',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery && idQuery.id) {
            body.andQ = { id: idQuery.id };
        } else if (trimmedSearchTerm) {
            body.orQ = {
                code: trimmedSearchTerm,
                fullname: trimmedSearchTerm
            };
        }

        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
    // virtualScroll: auto-enabled (required for SERVER_SIDE)
    // lazy: auto-enabled (required for SERVER_SIDE)
};

// Learner By Course Multi-Select Configuration
export const LEARNER_BY_COURSE_MULTISELECT_CONFIG: MultiSelectConfig = {
    dataLoadMode: DataLoadMode.GET_ALL,
    service: null, // Will be injected
    method: 'getLearnersByCourse',
    idField: 'id',
    labelFormatter: (learner: any) => `${learner.code} - ${learner.fullname}`,
    valueField: 'id',
    placeholder: 'Chọn học viên',
    sortField: 'code,asc',
    templateName: 'learner',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        // Return courseCode string directly (not an object) as getLearnersByCourse expects courseCode as second param
        if (baseQuery && baseQuery.courseCode) {
            return baseQuery.courseCode;
        }
        return '';
    }
    // virtualScroll: auto-enabled (required for SERVER_SIDE)
    // lazy: auto-enabled (required for SERVER_SIDE)
};

// Assignee (Employee) Multi-Select Configuration
export const ASSIGNEE_MULTISELECT_CONFIG: MultiSelectConfig = {
    dataLoadMode: DataLoadMode.GET_PAGINATED,
    filterMode: FilterMode.SERVER_SIDE,
    service: null, // Will be injected
    method: 'getEmployeeOptions',
    idField: 'id',
    labelFormatter: (employee: any) => `${employee.code} - ${employee.fullname || 'N/A'} (${employee.nickname})`,
    valueField: 'id',
    placeholder: 'Chọn người phụ trách',
    sortField: 'code,asc',
    templateName: 'assignee',
    placeholderSubstitute: true,
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery && idQuery.id) {
            body.andQ = { id: idQuery.id };
        } else if (trimmedSearchTerm) {
            body.orQ = {
                code: trimmedSearchTerm,
                fullname: trimmedSearchTerm,
                nickname: trimmedSearchTerm
            };
        }

        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
    // virtualScroll: auto-enabled (required for SERVER_SIDE)
    // lazy: auto-enabled (required for SERVER_SIDE)
};

